import * as R from 'ramda';
import moment from 'moment';

const sn = window.sn;

export function prettyBoolean(value) {
    return value ? 'True' : 'False';
}

/**
 * @export
 * @param {number} number
 * @param {boolean} [showCents=true]
 * @return {*}  {string}
 */
export function prettyDollars(number, showCents = true) {
    const n = (typeof number === 'string' ? parseFloat(number) : number) ?? 0;
    const s = showCents
        ? n.toFixed(2).replace(/(\d)(?=(\d{3})+\.)/g, '$1,')
        : Math.floor(n)
              .toFixed(0)
              .replace(/(\d)(?=(\d{3})+\.)/g, '$1,');
    return `$${s}`;
}

/**
 * @export
 * @param {string} ageRange
 * @return {*}  {string}
 */
export function prettyAgeRange(ageRange) {
    return ageRange
        .split(',')
        .map(age => {
            if (age === 'all') return sn.CampaignAudienceAgeMap.all;
            else if (age === '13_to_17') return sn.CampaignAudienceAgeMap['13_to_17'];
            else if (age === '18_to_24') return sn.CampaignAudienceAgeMap['18_to_24'];
            else if (age === '25_to_34') return sn.CampaignAudienceAgeMap['25_to_34'];
            else if (age === '35_to_44') return sn.CampaignAudienceAgeMap['35_to_44'];
            else if (age === '45_to_54') return sn.CampaignAudienceAgeMap['45_to_54'];
            else if (age === '55_plus') return sn.CampaignAudienceAgeMap['55_plus'];
        })
        .join(', ');
}

/**
 * @export
 * @param {string} gender
 * @return {*}  {string}
 */
export function prettyGender(gender) {
    if (gender === 'all') return sn.CampaignAudienceGenderMap.all;
    else if (gender === 'male') return sn.CampaignAudienceGenderMap.male;
    else if (gender === 'female') return sn.CampaignAudienceGenderMap.female;
}

/**
 * @export
 * @param {string} location
 * @return {*}  {string}
 */
export function prettyLocation(location) {
    if (location === 'us') return sn.CampaignAudienceLocationMap.us;
    else if (location === 'us_canada') return sn.CampaignAudienceLocationMap.us_canada;
    else if (location === 'us_europe') return sn.CampaignAudienceLocationMap.us_europe;
    else if (location === 'europe') return sn.CampaignAudienceLocationMap.europe;
    else if (location === 'worldwide') return sn.CampaignAudienceLocationMap.worldwide;
}

/**
 * @export
 * @param {string} str
 * @param {number} len
 * @return {*}  {string}
 */
export function truncate(str, len) {
    if (!str || str.length < len) return str;
    return `${str.substring(0, len)}...`;
}

/**
 * @export
 * @param {number} number
 * @param {number} [precision=null]
 * @return {*}  {string}
 */
export function toFixedPrecision(number, precision = null) {

    const n = (typeof number === 'string' ? parseFloat(number) : number) ?? 0;

    if (precision === null) {
        precision = 2;
    }

    return n.toFixed(precision);
}

/**
 * Abbreviates numbers that are greater than 999.
 * @export
 * @param {number} number
 * @return {*}
 */
export function scaleNumber(number) {
    if (number >= 0 && number <= 999) {
        return number;
    }

    return prettyNumber(number, 1);
}

/** This will not show any decimal points. */
export function scaleInteger(number) {
    if (number === 0) {
        return 0;
    }

    if (number < 1 && number > 0) {
        return 0;
    }

    if (number < 0) {
        return `-${largeIntegerToSuffix(Math.abs(number))}`;
    }

    return largeIntegerToSuffix(number);
}

export function largeIntegerToSuffix(number = 0, precision = 2) {
    return R.reduce(
        (accum, [suffix, threshold]) => {
            return typeof threshold === 'number' && number >= threshold
                ? `${(number / threshold).toFixed(precision)}${suffix}`
                : accum;
        },
        number.toString(),
        [
            ['k', 1000],
            ['M', 1000000],
            ['B', 1000000000],
            ['T', 1000000000000]
        ]
    );
}

/**
 * @export
 * @param {number} number
 * @param {number} [precision=null]
 * @return {*}  {string}
 */
export function prettyNumber(number, precision = null) {
    const n = (typeof number === 'string' ? parseFloat(number) : number) ?? 0;

    if (precision === null) {
        precision = 2;
    }

    if (n < 0) {
        return n.toFixed(precision);
    }

    if (n < 1) {
        return n < 0.01 ? n.toFixed(3) : n.toFixed(precision);
    }
    if (n < 1000) {
        return n.toFixed(precision);
    }

    return largeIntegerToSuffix(n, precision);
}

export function prettyPercentage(item) {
    return R.pipe(R.multiply(100), prettyNumber)(item);
}

export function isNotNil(item) {
    return R.pipe(R.isNil, R.not)(item);
}

export function isUnknown(item) {
    return R.pipe(R.toLower, R.equals('unknown'))(item);
}

export function isNotNilAndIsNotEmpty(val) {
    return R.and(isNotNil(val), !R.isEmpty(val));
}

/**
 * @export
 * @param {number} x
 * @return {*}  {string}
 */
export function numberWithCommas(x) {
    if (R.isNil(x)) {
        return '0';
    }

    const parts = x.toString().split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
}

/**
 * @export
 * @param {number} value
 * @param {number} [precision=2]
 * @return {*}  {string}
 */
export function percent(value, precision = 2) {
    if (R.isNil(value)) {
        return '0%';
    }
    return `${(value * 100).toFixed(precision)}%`;
}

/**
 * @export
 * @param {string} value
 * @return {*}  {number}
 */
export function parseIntBase64Safe(value) {
    try {
        return parseInt(atob(value), 10);
    } catch (e) {
        return 0;
    }
}

/**
 * @export
 * @param {string} str
 * @return {*}
 */
export function toTitleCase(str) {
    if (R.isNil(str)) {
        return str;
    }

    return str.replace(/\w\S*/g, function(txt) {
        return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });
}

/**
 * @param {string} term
 * @return {*}  {boolean}
 */
function isPrefixed(term) {
    if (term[0] === '#') return true;
    else if (term[0] === '@') return true;
    return false;
}

export function formatHashtags(hashtags) {
    if (hashtags && hashtags.length) {
        return R.pipe(
            R.split(','),
            R.map(h => (isPrefixed(h) ? h : `#${h}`)),
            R.join(' ')
        )(hashtags);
    }
    return '';
}

export function parseHashtagLabels(hashtags) {
    if (hashtags && hashtags.length) {
        return R.pipe(
            R.split(','),
            R.map(h => (isPrefixed(h) ? h.replace(/#/g, '') : h)),
            R.join(' ')
        )(hashtags);
    }
    return '';
}

export function formatMentions(mentions) {
    if (mentions && mentions.length) {
        return R.pipe(
            R.split(','),
            R.map(m => (isPrefixed(m) ? m : `@${m}`)),
            R.join(' ')
        )(mentions);
    }
    return '';
}

export function parseMentionLabels(mentions) {
    if (mentions && mentions.length) {
        return R.pipe(
            R.split(','),
            R.map(h => (isPrefixed(h) ? h.replace(/@/g, '') : h)),
            R.join(' ')
        )(mentions);
    }
    return '';
}

export function daysUntilDate(date) {
    const now = moment();
    const end = moment.unix(date);

    return end.diff(now, 'days');
}

export function englishList(items) {
    if (!items || !items.length || items.length < 2) {
        return items;
    }
    if (items.length === 2) {
        return R.intersperse(' and ', items);
    }

    const withCommas = R.intersperse(', ', items);
    return R.flatten([R.init(withCommas), ['and '], R.last(withCommas)]);
}

// Get a random number, toString it and take the first 5 characters
export function randomId() {
    return Math.random()
        .toString(36)
        .substring(5);
}



// WEBPACK FOOTER //
// ./common/string_helper.js